import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, CircularProgress, Stack } from "@mui/material";
import { tokens, withAlpha } from "../../../Common/designTokens";
import { formatListId } from "../../../Utils/listOrderId";
import { isFinishedListItem } from "../../../Utils/listRfqStatus";
import RfqFinalConfirmPopup from "./RfqFinalConfirmPopup";

/**
 * Confirm before pulling an RFQ, showing what is about to be cancelled.
 *
 * Two ways to feed it, and they can be combined:
 *
 *   details   one flat set of label/value boxes. This is the original behaviour and what
 *             the single-bond Cancel uses, unchanged.
 *   items     the bonds of a List RFQ, drawn as a compact read-only table underneath.
 *             Identifier, side, quantity, price and status - enough to show WHAT is about to
 *             be pulled, without reproducing the blotter. Ids are deliberately left out even
 *             though the API returns them; status is NOT, because a cancel does not touch a
 *             bond that has already finished, and a table that hid that was telling the
 *             trader they were cancelling more than they really were. See
 *             LIST_FINISHED_STATUSES.
 *
 * <p><b>Two different cancels, two different gates.</b> Pass `onConfirmList` and the dialog
 * grows a third button beside No and Yes, which is how a List RFQ is cancelled - there is no
 * separate Cancel List button on the blotter row. The two actions are NOT the same request
 * and are NOT gated by the same thing:
 * <ul>
 *   <li><b>Yes</b> cancels ONLY the bond that was clicked, so it is gated by that bond's own
 *       status: handleYes reads rfqStatus out of `details` and refuses TRADED / EXPIRED /
 *       MANUAL_TAKE_OVER. This is the plain single-bond cancel, unchanged, and it is right
 *       that a finished bond blocks it.</li>
 *   <li><b>Cancel List</b> pulls every bond in the list, so this bond's status is irrelevant
 *       to it - what matters is whether ANY bond is still live. It is gated by `items`
 *       instead: if every bond listed has already finished the server would refuse the
 *       request, so the dialog says so rather than sending it. A partly-finished list
 *       confirms normally; only the live bonds are acted on.</li>
 * </ul>
 * Keeping those two gates apart is the point. Gating Yes on the list would stop a trader
 * cancelling the one live bond they clicked in a list that is otherwise done; gating Cancel
 * List on this bond would stop them pulling the others from a row that had traded.
 */
// Which bonds a list cancel will not touch - shared with the read-only list view's Cancel List
// button, see Utils/listRfqStatus.

const RfqCancellationConfirmPopup = ({ open, onClose, onConfirm, onConfirmList, singleCancelDisabled = false, orderId, accountId, message, details = {}, items = [], itemsLoading = false }) => {
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  // Which cancel is waiting on the second "are you sure" popup: "single", "list" or null.
  const [pendingAction, setPendingAction] = useState(null);

  const BLOCKED_STATUSES = new Set(["MANUAL_TAKE_OVER", "TRADED", "EXPIRED"]);

  // Which of the listed bonds a LIST cancel will actually act on. Before this split the table
  // drew a traded bond identically to a working one, so "cancel these 3" could really mean
  // "cancel 2 of these 3" - the dialog itself was the misleading part.
  //
  // nothingCancellable gates the Cancel List button and nothing else. It must never reach
  // handleYes: that cancels the one bond the trader clicked, which can perfectly well be the
  // last live bond in an otherwise finished list.
  const cancellableItems = items.filter((item) => !isFinishedListItem(item.status));
  const nothingCancellable =
    !itemsLoading && items.length > 0 && cancellableItems.length === 0;

  // The list's own number, for the table heading below. Every item carries the same value -
  // it identifies the list, not the bond - so the first one that has it answers for all of
  // them. Falls back to the wording used before list numbers existed, so a list raised back
  // then still reads as a sentence rather than as "Bonds in ---".
  const listNumber = items.find((item) => item.listId !== null
    && item.listId !== undefined)?.listId;
  // The list's name when it has one, then its number, then the old wording.
  const listName = items.find((item) => item.listName)?.listName;
  const listLabel = listName
    || (listNumber === undefined ? "this list" : formatListId(listNumber));

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setListLoading(false);
      setPendingAction(null);
    }
  }, [open]);

  // reset block state when modal opens or when details change
  useEffect(() => {
    if (open) {
      setCancelDisabled(false);
      setStatusMessage("");
    } else {
      setCancelDisabled(false);
      setStatusMessage("");
    }
  }, [open, details?.rfqStatus, details?.status]);

  // Cancels ONLY the bond that was clicked. Deliberately does NOT consult nothingCancellable:
  // that describes the rest of the list, and this button has nothing to do with the rest of
  // the list. See the two-gates note at the top of the file.
  const handleYes = () => {
    // check RFQ status first; block cancellation for specific statuses
    const status = mergedDetails?.rfqStatus ?? mergedDetails?.status;
    if (status && BLOCKED_STATUSES.has(status)) {
      // Use protocolLabel (which already contains the RFQ suffix when applicable)
      setStatusMessage(`The status of ${protocolLabel} is ${status}. You can no longer cancel this ${protocolLabel}.`);
      setCancelDisabled(true);
      return;
    }
    // Allowed: ask "are you sure" in the second popup before anything is sent.
    setPendingAction("single");
  };

  // Runs the single-bond cancel once the trader confirms it in the second popup.
  const confirmSingleCancel = async () => {
    if (!onConfirm) {
      onClose(false);
      return;
    }
    try {
      setLoading(true);
      await onConfirm(); // parent handles API call and snackbar
      setLoading(false);
      onClose(true);
    } catch (err) {
      setLoading(false);
      // allow parent to show errors; keep dialog open or close based on parent behavior
    }
  };

  /**
   * Cancels the WHOLE list this bond belongs to.
   *
   * Mirrors handleYes above step for step - same missing-handler fallback, same loading
   * flag, same close-on-success, same swallow-and-stay-open on error so the parent can
   * show the failure. Only the gate differs, and that difference is the whole point: this
   * one asks "is any bond of the list still live?" where handleYes asks "is THIS bond
   * still live?".
   */
  const handleCancelList = () => {
    // Every bond of the list has already finished. The server would refuse this request, so
    // say so here instead of sending it and surfacing a backend error.
    if (nothingCancellable) {
      setStatusMessage(
        "Every bond in this list has already finished, so there is nothing left to cancel."
      );
      setCancelDisabled(true);
      return;
    }
    // Allowed: ask "are you sure" in the second popup before anything is sent.
    setPendingAction("list");
  };

  // Runs the whole-list cancel once the trader confirms it in the second popup.
  const confirmListCancel = async () => {
    if (!onConfirmList) {
      onClose(false);
      return;
    }
    try {
      setListLoading(true);
      await onConfirmList(); // parent handles API call and snackbar
      setListLoading(false);
      onClose(true);
    } catch (err) {
      setListLoading(false);
    }
  };

  const handleNo = () => {
    if (loading || listLoading) return;
    onClose(false);
  };

  // "No" in the second popup: back to the first one, nothing sent.
  const handleConfirmNo = () => {
    if (loading || listLoading) return;
    setPendingAction(null);
  };

  const handleOk = () => {
    // clear block state and close
    setCancelDisabled(false);
    setStatusMessage("");
    onClose(false);
  };

  // Helper: format key to friendly label (simple title-case + preserve uppercase acronyms)
  const formatKey = (k) => {
    if (!k) return k;
    const map = {
      protocol: "Protocol",
      rfqStatus: "RFQ Status",
      inquiryType: "Inquiry Type",
      rfqInquiryType: "Inquiry Type",
      quoteSubmissionDeadline: "Quote Deadline (min)",
      rfqExpiryTime: "RFQ Expiry(UTC)",
      priceType: "Price Type",
      stipulationType: "Stipulation Type",
      allowPartialExecution: "Allow Partial Exec",
      // List RFQ line items - the per-bond key and the fields shown beside it.
      tradingSessionSubId: "Line Item ID (625)",
      listId: "List ID",
      listName: "List Name",
      quantity: "Quantity",
      venue: "Dealers",
      ttl: "Quote Firm Deadline (min)",
      quoteExpiryDtTime: "Expiry(UTC)",
      // new mappings for MarketAxess / rejection
      mktxTargetLevel: "MarketAxess Target Level",
      mktxPricingProcess: "MarketAxess Pricing Process",
      mktxDealerListMode: "MarketAxess Dealer List Mode",
    };
    if (map[k]) return map[k];
    const spaced = k.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_\-\.\:]+/g, " ");
    return spaced.split(" ").map(w => (w.toUpperCase() === w ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
  };
  
  // Helper: simple value formatting + truncation for long strings
  const formatValue = (v, key) => {
    if (v === null || v === undefined || v === "") return null;

    // Special-case: show only time (HH:MM:SS) in UTC for rfqExpiryTime
    // Normalize ISO-like strings without timezone: treat them as UTC by appending 'Z'
    const normalizeIsoAsUtc = (s) => {
      if (typeof s !== "string") return s;
      const hasT = s.indexOf("T") !== -1;
      const hasZ = s.toUpperCase().endsWith("Z");
      const hasOffset = /[+-]\d{2}(:?\d{2})?$/.test(s);
      return hasT && !hasZ && !hasOffset ? s + "Z" : s;
    };

    if (key === "rfqExpiryTime" || key === "quoteExpiryDtTime") {
      try {
        const normalized = normalizeIsoAsUtc(v);
        const d = new Date(normalized);
        if (!isNaN(d)) {
          const pad = (n) => String(n).padStart(2, "0");
          const hh = pad(d.getUTCHours());
          const mm = pad(d.getUTCMinutes());
          const ss = pad(d.getUTCSeconds());
          return `${hh}:${mm}:${ss}`;
        }
      } catch (e) {
        // fallthrough to generic formatting
      }
    }

    // MarketAxess numeric formatting: show with up to 2 decimals
    if (key === "mktxTargetLevel") {
      const num = Number(v);
      if (!isNaN(num)) {
        return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
    }

    // Dates: small heuristic to show readable date/time for other date-like strings
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      try {
        const d = new Date(normalizeIsoAsUtc(v));
        if (!isNaN(d)) return d.toUTCString();
      } catch (e) {}
    }

    // Format account objects concisely
    if (key === "account" && typeof v === "object") {
      return v.accountId || v.id || v.name || JSON.stringify(v);
    }
    // Prefer human-readable for accountId/orderId/cusip/userName
    if (key === "accountId" || key === "orderId" || key === "cusip" || key === "userName") {
      return String(v);
    }

    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return s.length > 60 ? s.slice(0, 57).trimEnd() + "..." : s;
  };

  // Merge provided details with orderId/accountId (prefer values in details)
  const mergedDetails = {
    ...details
  };

  // Dynamic protocol label for title (fallback to Trumid)
  // Protocol strings already include the "RFQ" suffix — do not append "RFQ" again.
  const protocolLabel = mergedDetails?.protocol ? String(mergedDetails.protocol) : "Trumid RFQ";

  // A list cancel is offered whenever the caller supplies a handler for it.
  const isListCancel = typeof onConfirmList === "function";

  // One heading for both cases. It briefly named the bond and the list-cancel option, but the
  // detail grid below already shows Identifier and List ID, so the longer wording was saying
  // twice what the dialog shows once.
  const modalTitle = `Confirm Cancel ${protocolLabel}`;

  // Build list of entries: only include non-empty/non-null values
  const detailEntries = Object.entries(mergedDetails)
    .filter(([k, v]) => v !== undefined && v !== null && v !== "");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900, // increased width slightly to better accommodate 3 columns
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    alignItems: "center",
  };
  
  // Add styles to render detail boxes similar to BondDetailsPanel
  const rfqInfoBoxStyle = {
    bgcolor: tokens.colors.defaultGridRowColor,
    borderRadius: "8px",
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    px: 1,
    py: 0.75,
    // ensure uniform sizing and allow wrapping inside
    boxSizing: "border-box",
    minWidth: 0, // allow grid to size columns evenly
    minHeight: 56,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    alignItems: "flex-start",
    justifyContent: "center",
  };

  const rfqInfoLabelStyle = {
    color: tokens.colors.fontColour85,
    mb: 0.1,
    fontSize: "0.75rem",
  };

  const rfqInfoValueStyle = {
    color: tokens.colors.fontColour,
    fontWeight: 400,
    fontSize: "0.9rem",
    // allow wrapping and preserving readability; long text will wrap and tooltip still shows full text
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  };

  /**
   * The label/value boxes for `details`. Kept as a function rather than inlined in the JSX
   * so the grid has one definition; it once also drew a `sections` prop, which the standalone
   * Cancel List dialog used before both cancels were merged into this one dialog.
   */
  const renderDetailGrid = (entries) => (
    <Box
      sx={{
        width: "100%",
        // fixed 3-column grid for uniform layout
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
        gap: 1,
        justifyItems: "stretch",
        alignItems: "start",
        border: `1px solid ${withAlpha(tokens.colors.bodyBorderColor50, 0.5)}`,
        p: 1,
        borderRadius: "8px",
      }}
    >
      {entries.map(([k, v]) => {
        const fv = formatValue(v, k);
        if (fv === null) return null;
        return (
          <Box key={k} sx={rfqInfoBoxStyle}>
            <Typography variant="caption" sx={rfqInfoLabelStyle}>
              {formatKey(k)}
            </Typography>
            <Typography variant="body2" sx={rfqInfoValueStyle} title={String(fv)}>
              {fv}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Modal open={open} onClose={handleNo} aria-labelledby="rfq-cancel-confirmation">
      <Box sx={style}>
        <Typography id="rfq-cancel-confirmation" variant="h6" sx={{ color: tokens.colors.fontColour }}>
          {modalTitle}
        </Typography>

        {/* Show non-null details first (if any) */}
        {detailEntries.length > 0 && renderDetailGrid(detailEntries)}

        {/* The bonds of the list, read-only. Same bordered container and same label/value
            colours as the detail boxes above, laid out as rows because N bonds in boxes
            would not be scannable. Scrolls past roughly six bonds rather than growing the
            dialog off-screen.

            An empty list renders nothing at all: the dialog still works, it simply shows
            less. fetchListRfqItems returns [] rather than throwing precisely so a failure
            to enrich can never stop the trader cancelling. */}
        {(itemsLoading || items.length > 0) && (
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="caption"
              sx={{
                color: tokens.colors.fontColour85,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                display: "block",
                mb: 0.5,
              }}
            >
              {itemsLoading
                ? "Bonds in this list"
                : cancellableItems.length === items.length
                ? `Bonds in ${listLabel} (${items.length})`
                : `Bonds in ${listLabel} (${items.length}) - ${cancellableItems.length} will be cancelled`}
            </Typography>

            <Box
              sx={{
                width: "100%",
                border: `1px solid ${withAlpha(tokens.colors.bodyBorderColor50, 0.5)}`,
                borderRadius: "8px",
                p: 1,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {itemsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1.7fr 0.8fr 1fr 1fr 1.4fr",
                      gap: 1,
                      px: 1,
                      pb: 0.5,
                    }}
                  >
                    {["Identifier", "Side", "Quantity", "Price", "Status"].map((heading) => (
                      <Typography key={heading} variant="caption" sx={rfqInfoLabelStyle}>
                        {heading}
                      </Typography>
                    ))}
                  </Box>

                  {items.map((item, index) => {
                    // A bond that has already traded, been rejected or expired is not going to
                    // be cancelled by this action - the server leaves it alone. Dim it and show
                    // why, so the table matches what will actually happen.
                    const finished = isFinishedListItem(item.status);
                    return (
                    <Box
                      key={item.orderId ?? index}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.7fr 0.8fr 1fr 1fr 1.4fr",
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: "6px",
                        bgcolor: tokens.colors.defaultGridRowColor,
                        mb: 0.5,
                        opacity: finished ? 0.55 : 1,
                      }}
                    >
                      <Typography variant="body2" sx={rfqInfoValueStyle}>
                        {item.cusip || "---"}
                      </Typography>
                      <Typography variant="body2" sx={rfqInfoValueStyle}>
                        {item.side || "---"}
                      </Typography>
                      <Typography variant="body2" sx={rfqInfoValueStyle}>
                        {item.quantity === null || item.quantity === undefined
                          ? "---"
                          : Number(item.quantity).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={rfqInfoValueStyle}>
                        {item.price === null || item.price === undefined
                          ? "---"
                          : Number(item.price).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4,
                            })}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ...rfqInfoValueStyle, fontStyle: finished ? "italic" : "normal" }}
                      >
                        {item.status || "---"}
                      </Typography>
                    </Box>
                    );
                  })}
                </>
              )}
            </Box>
          </Box>
        )}

        {/* If cancellation is blocked due to RFQ status: show message and single OK button */}
        {statusMessage ? (
          <>
            <Typography variant="body2" color="error" sx={{ textAlign: "center", mt: 1 }}>
              {statusMessage}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleOk}
                sx={{ textTransform: "none", minWidth: 100 }}
              >
                OK
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* The "are you sure" prompt is no longer shown here - Yes and Cancel List each
                open a second popup that asks it (see the confirmation Modal below). */}
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleNo}
                disabled={loading}
                sx={{ textTransform: "none", minWidth: 100 }}
              >
                No
              </Button>
              {/* Contained, against Cancel List's outline - the two must not read as equally
                  weighted. This dialog is opened from one bond's Cancel button, so cancelling
                  that bond is the expected action and carries the solid fill. */}
              <Button
                variant="contained"
                color="error"
                onClick={handleYes}
                disabled={loading || listLoading || cancelDisabled || singleCancelDisabled}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ textTransform: "none", minWidth: 100 }}
              >
                {loading ? "Cancelling..." : "Yes"}
              </Button>

              {/* Offered only on a bond that belongs to a list, and it sits beside Yes rather
                  than on the blotter row: a row button could not say WHICH bonds it was about
                  to pull, which is exactly what the table above this shows.

                  Outlined, where Yes is contained, so the two are not offered as equals: this
                  one pulls the whole enquiry, so it is the heavier consequence and gets the
                  lighter treatment - deliberate rather than reachable by reflex.

                  Left enabled even when nothing in the list can be cancelled, so the click
                  can explain why instead of going dead with no reason given - the same thing
                  handleYes does for a blocked status, and the same idiom as the OK branch
                  above. handleCancelList holds the actual guard. */}
              {isListCancel && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelList}
                  disabled={loading || listLoading}
                  startIcon={
                    listLoading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                  sx={{ textTransform: "none", minWidth: 100 }}
                >
                  {listLoading ? "Cancelling..." : "Cancel List"}
                </Button>
              )}
            </Box>
          </>
        )}

        {/* Second popup: the "are you sure" step for whichever cancel was clicked. Nothing is
            sent until its Yes; its No returns to the dialog above. */}
        <RfqFinalConfirmPopup
          open={pendingAction !== null}
          message={
            pendingAction === "list"
              ? `Are you sure you want to cancel ${listLabel}?`
              : message || `Are you sure you want to cancel this ${protocolLabel}?`
          }
          loading={loading || listLoading}
          onNo={handleConfirmNo}
          onYes={pendingAction === "list" ? confirmListCancel : confirmSingleCancel}
        />
      </Box>
    </Modal>
  );
};

export default RfqCancellationConfirmPopup;
